import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  IconButton,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../../../config/config';

const ControllerEditDialog = ({ open, handleClose, device, refreshData }) => {
    console.log('ControllerEditDialog rendered with device:', device);

  /* ─── form state ─── */
  const [deviceName, setDeviceName] = useState('');
  const [contactNos, setContactNos] = useState(['']);

  /* association fields */
  const [associateControllerFlowMeter, setAssociateControllerFlowMeter] = useState(false);
  const [controllerFlowMeters, setControllerFlowMeters] = useState([{ deviceCode: '', sensorSubId: '' }]);

  /* misc */
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);

  /* ─── role detection ─── */
  const [userRole, setUserRole] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const tkn = localStorage.getItem('token');
    if (tkn) {
      try { 
        const decoded = jwtDecode(tkn);
        const role = decoded.userType || '';
        setUserRole(role);
        setIsAdmin(role === 'Admin');
      } catch { /* ignore */ }
    }
  }, []);

  /* ─── sync when device prop changes ─── */
  useEffect(() => {
    if (!device) return;
    
    setDeviceName(device.sensor_name || '');
    setContactNos(
      Array.isArray(device.contact_no) && device.contact_no.length
        ? device.contact_no.map(n => String(n))
        : ['']
    );

    // Load existing controller flow meter associations
    fetchControllerAssociations();
  }, [device]);

  /* ─── refresh associations when dialog opens ─── */
  useEffect(() => {
    if (open && device) {
      fetchControllerAssociations();
    }
  }, [open, device]);

  /* ─── fetch existing controller associations ─── */
  const fetchControllerAssociations = async () => {
    if (!device?.device_code) return;

    console.log('Fetching controller associations for device:', device.device_code);
    try {
      const response = await axios.get(
        `${API_BASE_URL}api/sensor/controller/${device.device_code}/flowmeter`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      console.log('Controller associations response:', response.data);
      if (response.data.hasControllerFlowMeters && response.data.controllerFlowMetersData.length > 0) {
        setAssociateControllerFlowMeter(true);
        setControllerFlowMeters(response.data.controllerFlowMetersData.map(fm => ({
          deviceCode: fm.deviceCode || '',
          sensorSubId: fm.sensorSubId || ''
        })));
      } else {
        setAssociateControllerFlowMeter(false);
        setControllerFlowMeters([{ deviceCode: '', sensorSubId: '' }]);
      }
    } catch (error) {
      console.error('Error fetching controller associations:', error);
      setAssociateControllerFlowMeter(false);
      setControllerFlowMeters([{ deviceCode: '', sensorSubId: '' }]);
    }
  };

  /* ─── helpers ─── */
  const handleSnackbarClose = () => setSnackbar(s => ({ ...s, open: false }));
  const addContactField = () => setContactNos(p => [...p, '']);
  const changeContactNo = (i, v) => setContactNos(p => p.map((n, idx) => (idx === i ? v : n)));

  /* ─── controller flow meter association helpers ─── */
  const handleAddControllerFlowMeter = () => {
    setControllerFlowMeters(prev => [...prev, { deviceCode: '', sensorSubId: '' }]);
  };

  const handleControllerFlowMeterChange = (idx, field, value) => {
    const arr = [...controllerFlowMeters];
    arr[idx][field] = value;
    setControllerFlowMeters(arr);
  };

  const handleRemoveControllerFlowMeter = (idx) => {
    if (controllerFlowMeters.length > 1) {
      const arr = controllerFlowMeters.filter((_, i) => i !== idx);
      setControllerFlowMeters(arr);
    }
  };

  /* ─── submit ─── */
  const handleSubmit = async () => {
    const e = {};

    if (!deviceName.trim()) e.deviceName = 'Required';

    const validContacts = contactNos
      .map(n => (typeof n === 'string' ? n.trim() : ''))
      .filter(Boolean);
    if (!validContacts.length) e.contactNos = 'At least one contact';
    else if (validContacts.some(n => !/^\+?\d+$/.test(n)))
      e.contactNos = 'Numeric only';

    // Validate controller flow meter associations
    if (associateControllerFlowMeter) {
      const validControllerFlowMeters = controllerFlowMeters.filter(cfm => 
        cfm.deviceCode.trim() !== '' && cfm.sensorSubId.trim() !== ''
      );
      if (validControllerFlowMeters.length === 0) {
        e.controllerFlowMeters = 'At least one flow meter with device code and sensor sub ID required';
      }
    }

    setErrors(e);
    if (Object.keys(e).length) return;

    setLoading(true);

    try {
      // Find the controller sensor for this device
      const sensorsResponse = await axios.get(
        `${API_BASE_URL}api/sensor/${device.device_code}/sensors`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      const controllerSensor = sensorsResponse.data.sensors.find(s => s.sensor_type === 'Controller');
      
      if (!controllerSensor) {
        throw new Error('Controller sensor not found for this device');
      }

      const payload = {
        sensor_name: deviceName,
        contact_no: validContacts,
      };

      // Add controller association data
      if (associateControllerFlowMeter) {
        const validControllerFlowMeters = controllerFlowMeters.filter(cfm => 
          cfm.deviceCode.trim() !== '' && cfm.sensorSubId.trim() !== ''
        );
        payload.associateControllerFlowMeter = true;
        payload.controllerFlowMeters = validControllerFlowMeters;
      } else {
        payload.associateControllerFlowMeter = false;
        payload.controllerFlowMeters = [];
      }

      await axios.put(`${API_BASE_URL}api/sensor/edit/${controllerSensor._id}`, payload);
      
      console.log('Controller updated successfully, payload was:', payload);
      setSnackbar({ open: true, message: 'Controller updated successfully', severity: 'success' });
      handleClose();
      if (refreshData) refreshData();
    } catch (err) {
      console.error('Error updating controller:', err);
      const msg = err.response?.data?.message || err.message || 'Update failed';
      setErrors({ apiError: msg });
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  /* ─── UI ─── */
  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Controller</DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Device Name */}
          <Grid item xs={12}>
            <TextField
              label="Device Name"
              value={deviceName}
              onChange={e => setDeviceName(e.target.value)}
              fullWidth
              required
              error={!!errors.deviceName}
              helperText={errors.deviceName}
            />
          </Grid>

          {/* Contacts */}
          {contactNos.map((c, i) => (
            <Grid item xs={12} key={i}>
              <TextField
                label={`Contact ${i + 1}`}
                value={c}
                onChange={e => changeContactNo(i, e.target.value)}
                fullWidth
                error={!!errors.contactNos}
                helperText={i === 0 && errors.contactNos}
              />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Button onClick={addContactField}>Add Contact</Button>
          </Grid>

          {/* Association Management */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              Flow Meter Association
            </Typography>
          </Grid>

          {/* Controller Flow Meter Association */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch 
                  checked={associateControllerFlowMeter} 
                  onChange={(e) => setAssociateControllerFlowMeter(e.target.checked)} 
                />
              }
              label="Associate Controller with Flow Meter"
            />
            {errors.controllerFlowMeters && (
              <Typography variant="caption" color="error" display="block">
                {errors.controllerFlowMeters}
              </Typography>
            )}
          </Grid>

          {associateControllerFlowMeter && (
            <Grid item xs={12}>
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Controller Flow Meters Association
                </Typography>
                {controllerFlowMeters.map((cfm, idx) => (
                  <Grid container spacing={2} key={idx} sx={{ mb: 2 }}>
                    <Grid item xs={5}>
                      <TextField
                        fullWidth
                        label="Flow Meter Device Code"
                        value={cfm.deviceCode}
                        onChange={(e) => handleControllerFlowMeterChange(idx, 'deviceCode', e.target.value)}
                        helperText="6-digit hexadecimal device code"
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <TextField
                        fullWidth
                        label="Flow Meter Sensor Sub ID"
                        value={cfm.sensorSubId}
                        onChange={(e) => handleControllerFlowMeterChange(idx, 'sensorSubId', e.target.value)}
                        helperText="2-digit sensor sub ID"
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        {controllerFlowMeters.length > 1 && (
                          <IconButton 
                            onClick={() => handleRemoveControllerFlowMeter(idx)}
                            color="error"
                            size="small"
                          >
                            <RemoveIcon />
                          </IconButton>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddControllerFlowMeter}
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Add Another Flow Meter
                </Button>
              </Box>
            </Grid>
          )}

          {/* API error */}
          {errors.apiError && (
            <Grid item xs={12}>
              <Alert severity="error">{errors.apiError}</Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined">Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ControllerEditDialog;
